import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import {
  ProvedorAssinatura,
  ResgateConfiguracaoStatus,
  ResgateDocumento,
  ResgateDocumentoOpcao,
} from '../../../core/models/parametros/resgate-configuracao.model';
import {
  RequerimentoOnlineConfiguracao,
  RequerimentoOnlineSalvo,
} from '../../../core/models/parametros/requerimento-online.model';
import {
  RequerimentoOnlineService,
  TipoRequerimentoNaoCadastradoError,
} from '../../../core/services/parametros/requerimento-online.service';
import { ToastService } from '../../../core/services/toast.service';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import { STATUS_CONFIGURACAO_OPCOES } from '../../../core/utils/parametros/resgate-configuracao.defaults';
import { vigenciaCoerenteValidator } from '../../../core/validators/parametros/resgate-configuracao.validators';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { TooltipAjudaComponent } from '../../../shared/components/tooltip-ajuda/tooltip-ajuda.component';
import { VivestCheckboxValueAccessorDirective } from '../../../shared/directives/checkbox-vivest/vivest-checkbox-value-accessor.directive';
import { VivestModalAreaVisivelDirective } from '../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestListaVisivelDirective } from '../../../shared/directives/select-vivest/vivest-lista-visivel.directive';
import { VivestSelectValueAccessorDirective } from '../../../shared/directives/select-vivest/vivest-select-value-accessor.directive';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { ResgateDocumentosModalComponent } from '../resgate-configuracao/resgate-documentos-modal/resgate-documentos-modal.component';

/** Limite de caracteres da descrição do requerimento online. */
export const REQUERIMENTO_DESCRICAO_MAXIMO = 500;

type DocumentoForm = FormGroup<{
  id: FormControl<number>;
  nome: FormControl<string>;
  marcado: FormControl<boolean>;
}>;

/**
 * Aba "Requerimento Online" do plano: status e vigência, descrição, assinatura digital e documentos
 * obrigatórios do requerimento. Mesmo layout e regras da Configuração do Resgate, incluindo o modal
 * "Adicionar documentos". Plano sem configuração gravada abre em branco, sem status nem última
 * atualização, até o primeiro salvar.
 */
@Component({
  selector: 'app-requerimento-online',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    EmptyStateComponent,
    TooltipAjudaComponent,
    ResgateDocumentosModalComponent,
    VivestCheckboxValueAccessorDirective,
    VivestModalAreaVisivelDirective,
    VivestSelectValueAccessorDirective,
    VivestListaVisivelDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './requerimento-online.component.html',
  styleUrl: './requerimento-online.component.scss',
})
export class RequerimentoOnlineComponent implements OnInit {
  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly service = inject(RequerimentoOnlineService);
  private readonly toastService = inject(ToastService);
  private readonly loaderService = inject(LoaderService);
  private readonly destroyRef = inject(DestroyRef);

  readonly planoId = input.required<number>();

  readonly statusOpcoes = STATUS_CONFIGURACAO_OPCOES;
  readonly descricaoMaximo = REQUERIMENTO_DESCRICAO_MAXIMO;

  readonly carregando = signal(true);
  readonly status = signal<ResgateConfiguracaoStatus | null>(null);
  /** Cor do `vivest-tag` do status salvo: Ativo em verde, Inativo em amarelo. */
  readonly varianteStatus = computed(() => (this.status() === 'Ativo' ? 'success' : 'warning'));
  readonly ultimaAtualizacao = signal<string | null>(null);

  readonly provedores = signal<ProvedorAssinatura[]>([]);
  readonly opcoesProvedor = computed(() =>
    this.provedores().map((provedor) => ({ value: String(provedor.id), label: provedor.nome })),
  );

  readonly modalDocumentosAberto = signal(false);
  readonly documentosDisponiveis = signal<ResgateDocumentoOpcao[]>([]);
  readonly carregandoDocumentos = signal(false);
  readonly confirmandoSalvar = signal(false);
  readonly salvando = signal(false);

  /** Última configuração carregada ou salva, para o "Cancelar" descartar as alterações. */
  private configuracaoAtual: RequerimentoOnlineConfiguracao | null = null;
  /** Tipo de requerimento da configuração carregada; sem ele (falha ao carregar) não há o que salvar. */
  private idTipoRequerimento: number | null = null;

  readonly form = this.fb.group({
    status: 'Ativo' as ResgateConfiguracaoStatus,
    vigencia: this.fb.group(
      {
        inicio: ['', Validators.required],
        /** Vazio: vigência sem data de término. */
        fim: '',
      },
      { validators: [vigenciaCoerenteValidator] },
    ),
    descricao: ['', Validators.maxLength(REQUERIMENTO_DESCRICAO_MAXIMO)],
    assinatura: this.fb.group({
      obrigatoria: false,
      provedor: ['', Validators.required],
      validacaoDuasEtapas: false,
    }),
    documentos: this.fb.array<DocumentoForm>([]),
  });

  get vigencia() {
    return this.form.controls.vigencia;
  }

  get descricao() {
    return this.form.controls.descricao;
  }

  get assinatura() {
    return this.form.controls.assinatura;
  }

  get documentos(): FormArray<DocumentoForm> {
    return this.form.controls.documentos;
  }

  /** Provedor obrigatório não escolhido, depois que o usuário passou pelo campo ou tentou salvar. */
  get provedorComErro(): boolean {
    const { provedor } = this.assinatura.controls;
    return provedor.touched && provedor.invalid;
  }

  ngOnInit(): void {
    this.assinatura.controls.obrigatoria.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((obrigatoria) => this.alternarAssinatura(obrigatoria));

    this.loaderService.showLoader();
    this.service
      .listarProvedores()
      .pipe(
        finalize(() => this.loaderService.hideLoader()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (provedores) => this.provedores.set(provedores),
        error: () => this.toastService.show('Não foi possível carregar os provedores de assinatura.', 'error'),
      });

    this.loaderService.showLoader();
    this.service
      .obterConfiguracao(this.planoId())
      .pipe(
        finalize(() => {
          this.carregando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (salvo) => this.exibir(salvo),
        error: (erro: unknown) =>
          this.toastService.show(
            erro instanceof TipoRequerimentoNaoCadastradoError
              ? erro.message
              : 'Não foi possível carregar a configuração do requerimento online.',
            'error',
          ),
      });
  }

  // ── documentos ──────────────────────────────────────────────────────────────

  /** Abre o modal; o catálogo de documentos é buscado na primeira abertura. */
  abrirModalDocumentos(): void {
    this.modalDocumentosAberto.set(true);
    if (this.documentosDisponiveis().length || this.carregandoDocumentos()) {
      return;
    }
    this.carregandoDocumentos.set(true);
    this.service
      .listarDocumentosDisponiveis()
      .pipe(
        finalize(() => this.carregandoDocumentos.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (documentos) => this.documentosDisponiveis.set(documentos),
        error: () => {
          this.modalDocumentosAberto.set(false);
          this.toastService.show('Não foi possível carregar os documentos disponíveis.', 'error');
        },
      });
  }

  fecharModalDocumentos(): void {
    this.modalDocumentosAberto.set(false);
  }

  /**
   * Inclui os documentos escolhidos no modal. Um documento que estava desmarcado na lista é
   * marcado de novo em vez de duplicado; os já marcados são ignorados.
   */
  adicionarDocumentos(opcoes: readonly ResgateDocumentoOpcao[]): void {
    let alterou = false;
    for (const opcao of opcoes) {
      const existente = this.documentos.controls.find((documento) => documento.controls.id.value === opcao.id);
      if (!existente) {
        this.documentos.push(this.criarDocumento({ id: opcao.id, nome: opcao.nome }));
        alterou = true;
      } else if (!existente.controls.marcado.value) {
        existente.controls.marcado.setValue(true);
        alterou = true;
      }
    }
    if (alterou) {
      this.documentos.markAsDirty();
    }
    this.modalDocumentosAberto.set(false);
  }

  /** Ids dos documentos marcados, para o modal mostrar como já incluídos. */
  idsDocumentos(): number[] {
    return this.documentos.controls
      .filter((documento) => documento.controls.marcado.value)
      .map((documento) => documento.controls.id.value);
  }

  // ── ações do rodapé ─────────────────────────────────────────────────────────

  /** Descarta as alterações não salvas, voltando à última configuração carregada ou salva. */
  cancelar(): void {
    if (this.configuracaoAtual) {
      this.aplicarConfiguracao(this.configuracaoAtual);
    }
  }

  /** Valida e pede confirmação; a gravação só acontece em `confirmarSalvar`. */
  salvar(): void {
    // O vivest-button repassa o clique mesmo desabilitado: ignora enquanto uma gravação corre.
    if (this.salvando()) {
      return;
    }
    if (this.idTipoRequerimento == null) {
      this.toastService.show('Não foi possível carregar a configuração do requerimento online.', 'error');
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.show('Revise os campos destacados antes de salvar.', 'error');
      return;
    }
    this.confirmandoSalvar.set(true);
  }

  cancelarSalvar(): void {
    this.confirmandoSalvar.set(false);
  }

  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    if (this.salvando() || this.idTipoRequerimento == null) {
      return;
    }

    this.salvando.set(true);
    this.loaderService.showLoader();
    this.service
      .salvarConfiguracao(this.planoId(), this.idTipoRequerimento, this.lerFormulario())
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (salvo) => {
          // Recarrega o que foi gravado: tira da lista os documentos desmarcados e limpa o "dirty".
          this.exibir(salvo);
          this.toastService.show('Configuração do requerimento online salva.', 'success');
        },
        error: () => this.toastService.show('Não foi possível salvar a configuração. Tente novamente.', 'error'),
      });
  }

  // ── helpers ─────────────────────────────────────────────────────────────────

  private exibir(salvo: RequerimentoOnlineSalvo): void {
    this.configuracaoAtual = salvo.configuracao;
    this.idTipoRequerimento = salvo.idTipoRequerimento;
    this.status.set(salvo.configurada ? salvo.configuracao.status : null);
    this.ultimaAtualizacao.set(salvo.ultimaAtualizacao ? formatarAtualizacao(salvo.ultimaAtualizacao) : null);
    this.aplicarConfiguracao(salvo.configuracao);
  }

  private aplicarConfiguracao(configuracao: RequerimentoOnlineConfiguracao): void {
    this.documentos.clear({ emitEvent: false });
    configuracao.documentos.forEach((documento) =>
      this.documentos.push(this.criarDocumento(documento), { emitEvent: false }),
    );
    this.form.reset({
      status: configuracao.status,
      vigencia: { inicio: configuracao.vigencia.inicio, fim: configuracao.vigencia.fim ?? '' },
      descricao: configuracao.descricao,
      assinatura: configuracao.assinatura,
    });
    // O reset pode não disparar `valueChanges` quando o valor de `obrigatoria` não muda.
    this.alternarAssinatura(configuracao.assinatura.obrigatoria);
  }

  /**
   * Valores do formulário no formato do model: fim de vigência vazio vira `null` e só os documentos
   * marcados entram (os desmarcados saem da configuração ao salvar).
   */
  private lerFormulario(): RequerimentoOnlineConfiguracao {
    const valor = this.form.getRawValue();
    return {
      ...valor,
      vigencia: { inicio: valor.vigencia.inicio, fim: valor.vigencia.fim || null },
      documentos: valor.documentos.filter((documento) => documento.marcado).map(({ id, nome }) => ({ id, nome })),
    };
  }

  /** `marcado` só existe na tela: desmarcado, o documento continua visível até salvar. */
  private criarDocumento(documento: ResgateDocumento): DocumentoForm {
    return this.fb.group({
      id: documento.id,
      nome: documento.nome,
      marcado: true as boolean,
    });
  }

  /**
   * Sem assinatura digital, provedor e validação em duas etapas deixam de valer: ficam
   * desabilitados, e a validação em duas etapas também é desmarcada.
   */
  private alternarAssinatura(obrigatoria: boolean): void {
    const { provedor, validacaoDuasEtapas } = this.assinatura.controls;
    if (!obrigatoria) {
      validacaoDuasEtapas.setValue(false, { emitEvent: false });
    }
    [provedor, validacaoDuasEtapas].forEach((control) =>
      obrigatoria ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false }),
    );
  }
}

/** `yyyy-MM-ddTHH:mm:ss` → `dd/MM/yyyy HH:mm`, sem passar por `Date` (evita deslocar o fuso). */
function formatarAtualizacao(isoDateTime: string): string {
  const [data, hora = ''] = isoDateTime.split('T');
  return `${formatDateIsoToBr(data)} ${hora.slice(0, 5)}`.trim();
}
